import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiAuth, useAxiosSwagger } from './useAxiosSwagger';
import { describeError } from '../api/requeue';
import { Dossier, getDossier } from '../api/dossier';

/** How long a dossier read for an opened issue is shown again without asking Chargebee and Exact once more. */
const FRESH_MS = 3 * 60_000;

const cache = new Map<string, { dossier: Dossier; at: number }>();

export interface IssueDossier {
    dossier: Dossier | null;
    loading: boolean;
    error: string | null;
    /** When it was read, for "live gelezen om". */
    readAt: number | null;
    /** Reads it again from Chargebee and Exact, past the cache. */
    reload: () => void;
}

/**
 * The dossier of the object behind an issue, read live from Chargebee and Exact when the issue is opened:
 * what the screen shows is how things stand now, not what the scanner saw last.
 *
 * Kept a few minutes per id, so opening and closing a row does not spend the Exact budget each time.
 */
export const useIssueDossier = (customer: string, environment: string, id: string | null | undefined): IssueDossier => {
    const axios = useAxiosSwagger();
    const { loading: tokenLoading } = useApiAuth();
    const key = id ? `${customer}/${environment}/${id}`.toLowerCase() : null;
    const cached = key ? cache.get(key) : undefined;

    const [dossier, setDossier] = useState<Dossier | null>(cached?.dossier ?? null);
    const [readAt, setReadAt] = useState<number | null>(cached?.at ?? null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const controller = useRef<AbortController | null>(null);

    const load = useCallback(
        (force: boolean) => {
            if (!axios || tokenLoading || !key || !id) {
                return;
            }

            const hit = cache.get(key);

            if (!force && hit && Date.now() - hit.at < FRESH_MS) {
                setDossier(hit.dossier);
                setReadAt(hit.at);
                return;
            }

            controller.current?.abort();
            const current = new AbortController();
            controller.current = current;

            setLoading(true);
            setError(null);

            getDossier(axios, customer, environment, { id, chargebee: true, exact: true }, current.signal)
                .then((result) => {
                    const at = Date.now();

                    cache.set(key, { dossier: result, at });

                    if (controller.current === current) {
                        setDossier(result);
                        setReadAt(at);
                    }
                })
                .catch((ex) => {
                    if (controller.current === current && !current.signal.aborted) {
                        setError(describeError(ex));
                    }
                })
                .finally(() => {
                    if (controller.current === current) {
                        controller.current = null;
                        setLoading(false);
                    }
                });
        },
        [axios, tokenLoading, key, id, customer, environment]
    );

    useEffect(() => {
        load(false);

        return () => controller.current?.abort();
    }, [load]);

    const reload = useCallback(() => load(true), [load]);

    return { dossier, loading, error, readAt, reload };
};
